// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import styles from '../styles/Address.style';
import BodyCopy from '../../../atoms/BodyCopy';

function getMaskedCharacters(string, length, startsCount) {
  let prefix = string.substring(0, length);
  prefix += '*'.repeat(startsCount);
  return prefix;
}

const getAddressfromDiffLines = (address, dataLocatorPrefix, isGuestUser) => {
  let addressLine1 = address.address1 || address.addressLine1;
  let addressLine2 = address.address2 || address.addressLine2 || '';
  if (isGuestUser) {
    addressLine1 = getMaskedCharacters(addressLine1, 4, 10);
    addressLine2 = addressLine2 ? getMaskedCharacters(addressLine2, 3, 3) : addressLine2;
  }

  return (
    <React.Fragment>
      <BodyCopy
        fontFamily="secondary"
        tag="p"
        data-locator={dataLocatorPrefix ? `${dataLocatorPrefix}-addressline1` : ''}
        className="address text-break "
      >
        {addressLine1}
      </BodyCopy>
      <BodyCopy
        fontFamily="secondary"
        tag="p"
        data-locator={dataLocatorPrefix ? `${dataLocatorPrefix}-addressline2` : ''}
        className="address text-break "
      >
        {addressLine2}
      </BodyCopy>
    </React.Fragment>
  );
};

/* eslint-disable react/prop-types */
const getAddessLines = ({ address, dataLocatorPrefix, showWarning, isGuestUser }) => {
  const isWarning = showWarning > 0;
  let addressLine1 = address?.address1 || address.addressLine1;
  let addressLine2 = address?.address2 || address.addressLine2 || '';
  if (isGuestUser) {
    addressLine1 = getMaskedCharacters(addressLine1, 4, 10);
    addressLine2 = addressLine2 ? getMaskedCharacters(addressLine2, 3, 3) : addressLine2;
  }
  return (
    <>
      <div className="warning-container">
        {isWarning && <BodyCopy component="p" className="warning-icon" />}
        <BodyCopy
          component="p"
          data-locator={dataLocatorPrefix ? `${dataLocatorPrefix}-addressl${addressLine1}` : ''}
          fontFamily="secondary"
          fontWeight={isWarning ? 'extrabold' : 'normal'}
          className="address text-break "
        >
          {addressLine1}
        </BodyCopy>
      </div>
      <div className="warning-container">
        {isWarning && <BodyCopy component="p" className="warning-icon" />}
        <BodyCopy
          component="p"
          data-locator={dataLocatorPrefix ? `${dataLocatorPrefix}-addressl${addressLine2}` : ''}
          fontFamily="secondary"
          fontWeight={isWarning ? 'extrabold' : 'normal'}
          className="address text-break "
        >
          {addressLine2}
        </BodyCopy>
      </div>
    </>
  );
};

// eslint-disable-next-line complexity
const getFormattedAddress = (address, dataLocatorPrefix, showWarning) => {
  const isWarning = showWarning === 3;
  const state = address?.stateCode || address?.state ? address?.stateCode || address?.state : '';
  const postal =
    address.postalCode || address.zip || address.zipCode
      ? address?.postalCode || address?.zip || address?.zipCode
      : '';
  const stateOrPostal =
    address?.postalCode || address?.zip || address?.zipCode || address?.stateCode || address?.state;

  return (
    <div className="warning-container">
      {isWarning && <BodyCopy component="p" className="warning-icon" />}
      <BodyCopy
        component="p"
        data-locator={dataLocatorPrefix ? `${dataLocatorPrefix}-cityfullname` : ''}
        fontFamily="secondary"
        fontWeight={isWarning ? 'extrabold' : 'normal'}
        className="address text-break"
      >
        {`${address.city ? `${address.city}` : ''}${stateOrPostal ? ', ' : ''}
          ${state}
        ${postal}`}
      </BodyCopy>
    </div>
  );
};

const getUserName = ({ address, isDefault, showDefault, isGuestUser }) => {
  if (isGuestUser) {
    return `${getMaskedCharacters(address.firstName, 3, 5)} ${getMaskedCharacters(
      address.lastName,
      1,
      9
    )}${isDefault && showDefault ? ' (Default)' : ''}`;
  }
  return `${address.firstName} ${address.lastName}${isDefault && showDefault ? ' (Default)' : ''}`;
};

/**
 * @function Address The address component will render an address
 * that is constructed from the address prop passed.
 * @param {string} className The class name for the component
 * @param {object} address address object
 */

export const Address = ({
  address,
  className,
  dataLocatorPrefix,
  fontWeight,
  showPhone,
  showCountry,
  isDefault,
  showName,
  showDefault,
  parentDataLocator,
  showWarning,
  spc,
  isInternationalPhoneNumberEnabled,
  isGuestUser,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  return address ? (
    <BodyCopy
      component="div"
      fontSize="fs14"
      color="text.primary"
      className={className}
      dataLocator={parentDataLocator}
    >
      {showName && (
        <BodyCopy
          component="p"
          id={address.addressId}
          fontWeight={!spc ? fontWeight : undefined}
          fontFamily="secondary"
          className="addressTile__name address text-break"
          data-locator={dataLocatorPrefix ? `${dataLocatorPrefix}-fullname` : ''}
        >
          {getUserName({ address, isDefault, showDefault, isGuestUser })}
        </BodyCopy>
      )}
      <BodyCopy component="div" className="addressLine">
        {address.address1 || address.addressLine1
          ? getAddessLines({ address, dataLocatorPrefix, showWarning, spc, isGuestUser })
          : getAddressfromDiffLines(address, dataLocatorPrefix, isGuestUser)}
        {getFormattedAddress(address, dataLocatorPrefix, showWarning)}
        {showCountry && (address?.countryCode || address?.country) && (
          <BodyCopy component="p" fontFamily="secondary" className="address text-break">
            {address.countryCode || address.country}
          </BodyCopy>
        )}
        {showPhone && address?.phone && (
          <BodyCopy component="p" fontFamily="secondary" className="address text-break">
            {isInternationalPhoneNumberEnabled &&
            address?.phoneCountryCode &&
            address?.phoneCountryCode !== '+1'
              ? `${address?.phoneCountryCode} `
              : ''}
            {address.phone}
          </BodyCopy>
        )}
      </BodyCopy>
    </BodyCopy>
  ) : null;
};

Address.propTypes = {
  address: PropTypes.shape({}),
  className: PropTypes.string,
  dataLocatorPrefix: PropTypes.string,
  fontWeight: PropTypes.string,
  showPhone: PropTypes.bool,
  showCountry: PropTypes.bool,
  isDefault: PropTypes.bool,
  showName: PropTypes.bool,
  showDefault: PropTypes.bool,
  parentDataLocator: PropTypes.string,
  showWarning: PropTypes.number,
  spc: PropTypes.bool,
};

Address.defaultProps = {
  showPhone: true,
  showCountry: true,
  isDefault: false,
  showName: true,
  address: null,
  className: '',
  dataLocatorPrefix: '',
  fontWeight: 'regular',
  showDefault: true,
  parentDataLocator: 'address-details',
  showWarning: 0,
  spc: false,
};

export default withStyles(Address, styles);
